/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Approval Modal — human-in-the-loop gate for workflow tools.
 *
 * Triggered by:
 *   1. SSE `tool.confirmation_requested` event → useWorkflowEvents
 *      surfaces it as pendingConfirmation; RunViewer auto-opens this
 *      modal.
 *   2. Operator clicks the inline "Approve" / "Reject" button on a
 *      stage card that's in awaiting_confirmation — same modal, but
 *      the confirmation_id comes from the stage rather than the event.
 *
 * Two-step reject: clicking the danger button first surfaces a reason
 * textarea so the operator can document WHY before the action lands in
 * the audit log. Approve is one-click.
 *
 * In Demo Mode we collapse the JSON-inputs preview and surface a
 * cleaned-up persona display name.
 */

import React, { useCallback, useEffect, useState } from 'react';
import workflowAdminApi from '../../../services/workflowAdminApi';
import { displayPersonaName, isDemoMode } from '../../../utils/demoMode';

export default function ApprovalModal({
  open,
  runId,
  request,           // { stage_id, tool_id, tool_description, tool_inputs,
                     //   confirmation_id, persona, intent? }
  onClose,
  onResolved,        // ({ approve, note, response }) called after success
}) {
  const demo = isDemoMode();
  const [phase, setPhase] = useState('idle');  // idle | confirming-reject | submitting | error
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [inputsExpanded, setInputsExpanded] = useState(false);

  // Reset internal state every time the modal opens for a new request.
  useEffect(() => {
    if (!open) return;
    setPhase(request?.intent === 'reject' ? 'confirming-reject' : 'idle');
    setNote('');
    setError(null);
    setInputsExpanded(false);
  }, [open, request]);

  // Escape closes (but not while submitting — losing a confirmation in
  // flight is a worse outcome than holding the user hostage briefly).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && phase !== 'submitting') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, phase, onClose]);

  const submit = useCallback(async (approve) => {
    if (!request?.confirmation_id) {
      setError('Missing confirmation_id — cannot resolve.');
      setPhase('error');
      return;
    }
    setPhase('submitting');
    setError(null);
    try {
      const response = await workflowAdminApi.confirmRun(
        runId,
        request.confirmation_id,
        { approve, note: note.trim() || undefined }
      );
      onResolved?.({ approve, note: note.trim() || null, response });
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error || err?.message || 'Failed to record decision.';
      setError(status ? `${status} — ${message}` : message);
      setPhase('error');
    }
  }, [runId, request, note, onResolved, onClose]);

  if (!open || !request) return null;

  const personaLabel = request.persona
    ? displayPersonaName(request.persona?.display_name || request.persona?.id || request.persona, demo)
    : null;

  return (
    <div className="wf-modal-scrim" role="dialog" aria-modal="true" aria-labelledby="wf-modal-title">
      <div className="wf-modal">
        <header className="wf-modal-header">
          <h2 id="wf-modal-title" className="wf-modal-title">
            Action required
          </h2>
          <button
            type="button"
            className="wf-modal-close"
            onClick={onClose}
            disabled={phase === 'submitting'}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="wf-modal-body">
          <p className="wf-modal-lede">
            Tool <strong>{request.tool_id || 'unknown'}</strong>
            {' '}requires confirmation before execution.
          </p>
          {request.tool_description && (
            <p className="wf-modal-description">{request.tool_description}</p>
          )}

          <dl className="wf-modal-meta">
            {personaLabel && (
              <>
                <dt>Persona</dt>
                <dd>{personaLabel}</dd>
              </>
            )}
            {request.stage_id && !demo && (
              <>
                <dt>Stage</dt>
                <dd>{request.stage_id}</dd>
              </>
            )}
            {request.confirmation_id && !demo && (
              <>
                <dt>Confirmation</dt>
                <dd className="wf-modal-mono">{request.confirmation_id}</dd>
              </>
            )}
          </dl>

          {request.tool_inputs && !demo && (
            <section className="wf-modal-inputs">
              <button
                type="button"
                className="wf-modal-inputs-toggle"
                onClick={() => setInputsExpanded((v) => !v)}
                aria-expanded={inputsExpanded}
              >
                {inputsExpanded ? '▾' : '▸'} Tool inputs
              </button>
              {inputsExpanded && (
                <pre className="wf-modal-inputs-payload">
                  {JSON.stringify(request.tool_inputs, null, 2)}
                </pre>
              )}
            </section>
          )}

          {phase === 'confirming-reject' && (
            <div className="wf-modal-reject-section">
              <label htmlFor="wf-modal-note" className="wf-form-label">
                Reason for rejection
              </label>
              <p className="wf-form-help">
                Recorded in the audit log alongside the rejection.
              </p>
              <textarea
                id="wf-modal-note"
                className="wf-form-input wf-form-textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why is this action being rejected?"
                disabled={phase === 'submitting'}
                autoFocus
              />
            </div>
          )}

          {error && (
            <div className="wf-form-error">{error}</div>
          )}
        </div>

        <footer className="wf-modal-footer">
          {phase === 'confirming-reject' ? (
            <>
              <button
                type="button"
                className="wf-btn wf-btn-secondary"
                onClick={() => { setPhase('idle'); setError(null); }}
                disabled={phase === 'submitting'}
              >
                Back
              </button>
              <button
                type="button"
                className="wf-btn wf-btn-danger"
                onClick={() => submit(false)}
                disabled={phase === 'submitting'}
              >
                {phase === 'submitting' ? 'Submitting…' : 'Confirm rejection'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="wf-btn wf-btn-secondary"
                onClick={onClose}
                disabled={phase === 'submitting'}
              >
                Cancel
              </button>
              <button
                type="button"
                className="wf-btn wf-btn-danger"
                onClick={() => setPhase('confirming-reject')}
                disabled={phase === 'submitting'}
              >
                Reject
              </button>
              <button
                type="button"
                className="wf-btn wf-btn-primary"
                onClick={() => submit(true)}
                disabled={phase === 'submitting'}
              >
                {phase === 'submitting' ? 'Approving…' : 'Approve'}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
